import {
  Card,
  Chip,
  DeleteIconButton,
  GeneralValidationErrors,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  formatPhoneNumber,
  ValidationErrors,
} from "@compass/core";
import { Select } from "components";
import { LkPersonTypeId, TextingOptInDto, TextingQueueDetailDto, TextingQueueItemDetailDto } from "generated/openApi";
import { useEffect, useState } from "react";
import ApiService from "services/apiService";
import { TextingStatusChip } from "../TextingStatusChip";

const getDeliveryStatus = (item: TextingQueueItemDetailDto) => {
  if (item.sent === true)
    return <TextingStatusChip status="Sent" />;

  if (item.sent === false)
    return <Chip label="Not Sent" color="warning" size="small" variant="outlined" />;

  return <Chip label="Pending" size="small" variant="outlined" />;
};

export const TextingQueueItems = (props: {
  textingQueue: TextingQueueDetailDto;
  setTextingQueue: (textingQueue: TextingQueueDetailDto) => void;
  isEditing: boolean;
  validationErrors: ValidationErrors;
}) => {
  const [optedInNumbers, setOptedInNumbers] = useState<TextingOptInDto[]>([]);

  useEffect(() => {
    if (!props.isEditing) return;

    if (props.textingQueue.personTypeId === LkPersonTypeId.Student) {
      ApiService.textingConversations.getOptedInStudentNumbers().then(setOptedInNumbers);
    } else if (props.textingQueue.personTypeId === LkPersonTypeId.Parent) {
      ApiService.textingConversations.getOptedInParentNumbers().then(setOptedInNumbers);
    }
  }, [props.isEditing, props.textingQueue.personTypeId]);

  const getPersonId = (option: TextingOptInDto) =>
    props.textingQueue.personTypeId === LkPersonTypeId.Parent ? option.parentId : option.studentId;
  const selectedPersonIds = new Set(
    props.textingQueue.items?.map((item) => item.personId).filter((id): id is number => id !== undefined) ?? []
  );
  const availableRecipients = optedInNumbers.filter((option) => {
    const personId = getPersonId(option);
    return personId !== undefined && !selectedPersonIds.has(personId);
  });

  const getRecipientName = (option: TextingOptInDto) =>
    props.textingQueue.personTypeId === LkPersonTypeId.Parent
      ? `${option.parentLastName || ""}, ${option.parentFirstName || ""}`.replace(/^, |, $/, "")
      : `${option.studentLastName || ""}, ${option.studentFirstName || ""}`.replace(/^, |, $/, "");

  const addRecipient = (option: TextingOptInDto | null | undefined) => {
    if (!option) return;

    const personId = getPersonId(option);
    if (!personId || selectedPersonIds.has(personId)) return;

    props.setTextingQueue({
      ...props.textingQueue,
      items: [
        ...(props.textingQueue.items ?? []),
        {
          id: 0,
          personId,
          personName: getRecipientName(option),
          identification: option.customId1,
          destinationPhoneNumber: option.phoneNumber,
          sent: false,
        },
      ],
    });
  };

  const removeRecipient = (item: TextingQueueItemDetailDto) => {
    props.setTextingQueue({
      ...props.textingQueue,
      items: props.textingQueue.items?.filter((existingItem) => existingItem !== item),
    });
  };

  return (
    <Card header={`Recipients (${props.textingQueue.items?.length || 0})`}>
      <Stack spacing="md">
        {props.isEditing && (
          <Grid container>
            <Grid item lg={5} md={7} sm={9} xs={12}>
              <Select
                label="Add Recipient"
                value={undefined as unknown as TextingOptInDto}
                options={availableRecipients}
                getOptionLabel={(option) => `${getRecipientName(option)} (${formatPhoneNumber(option.phoneNumber)})`}
                onChange={(event, value) => addRecipient(value)}
              />
            </Grid>
          </Grid>
        )}

        {props.validationErrors.recipients && (
          <GeneralValidationErrors errors={props.validationErrors.recipients} />
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Phone Number</TableCell>
                <TableCell>Message</TableCell>
                {props.isEditing && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {props.textingQueue.items?.map((item) => (
                <TableRow key={`${item.id}-${item.personId}`}>
                  <TableCell>{getDeliveryStatus(item)}</TableCell>
                  <TableCell>
                    <Stack>
                      <Typography variant="inherit">
                        {item.personName || "Recipient unavailable"}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{formatPhoneNumber(item.destinationPhoneNumber) || "-"}</TableCell>
                  <TableCell sx={{ maxWidth: 520, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                    {item.outputMessage || props.textingQueue.message || "-"}
                  </TableCell>
                  {props.isEditing && (
                    <TableCell align="right">
                      <DeleteIconButton onClick={() => removeRecipient(item)} size="small" />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Card>
  );
};
